import {ActionTypes, AutomationAction} from './actions';
import {AutomationContextType, defaultContext} from './model';
import {priority} from '@priority-software/priority-proto-api/lib';
import {mapAppliedFilters} from '../utils';
import {alertListFilter, isRuleAppliedToCurrentUser, updateIsActiveRule} from './utils';
import {RuleFilters, RuleType} from '../enums';
import {prepareListData} from '../components/RulesListDialog/parts/AutomationsOverview/utils';

export type AutomationReducerType = (
    state: AutomationContextType,
    action: AutomationAction
) => AutomationContextType;

export const automationReducer: AutomationReducerType = (state, action) => {
    switch (action.type) {
        case ActionTypes.OPEN_AUTOMATION:
            return {...state, isAutomationOpen: true};

        case ActionTypes.CLOSE_AUTOMATION:
            return {
                ...state,
                ...defaultContext,
                // Keep the automationType as it is for BPM rules when closing the automations
                automationType:
                    state.automationType === RuleType.bpm ? RuleType.bpm : RuleType.empty
            };

        case ActionTypes.UPDATE_LIST_RESPONSE:
            return {...state, listResponse: action.payload.data};

        case ActionTypes.OPEN_SINGLE_RULE:
            const {ruleNumber, CoverLetters, newEditorSupport} = action.payload;
            const initRuleData = state.listResponse.find(
                rule => rule.AlertNumber === ruleNumber
            ) || {
                Description: '',
                Active: true,
                Form: {
                    ValidMySelf: true
                },
                ConditionsOperator: false,
                AlertConds: [
                    new priority.netitems.form.AlertCondition({
                        CurrentValue: state.choosesOptions.CurrrentValues[0]?.value as number
                    })
                ]
            };
            // Update the openRule with CoverLetters
            const openRule = new priority.netitems.form.Alert({
                ...initRuleData,
                CoverLetters: CoverLetters || initRuleData.CoverLetters || []
            });

            return {...state, singleRuleOpen: true, openRule, newEditorSupport};

        case ActionTypes.UPDATE_OPEN_RULE:
            const {data} = action.payload;

            return {
                ...state,
                openRule: {
                    ...state.openRule,
                    ...data
                }
            };

        case ActionTypes.CLOSE_SINGLE_RULE:
            return {...state, singleRuleOpen: false, openRule: null, aiGeneratedRuleQuery: ''};

        case ActionTypes.SET_AUTOMATION_RULES:
            const automationRules = action.payload.rules;
            return {...state, automationRules};

        case ActionTypes.SET_AUTOMATION_CHOOSES:
            const choosesOptions = action.payload.choosesOptions;
            return {...state, choosesOptions};

        case ActionTypes.REMOVE_RULE:
            return {
                ...state,
                automationRules: state.automationRules.filter(
                    rule => rule.alertNumber !== action.payload.ruleNumber
                ),
                listResponse: state.listResponse.filter(
                    rule => rule.AlertNumber !== action.payload.ruleNumber
                )
            };
        case ActionTypes.SET_AUTOMATION_TYPE:
            return {...state, automationType: action.payload.type};

        case ActionTypes.SET_APPLIED_FILTERS:
            return {
                ...state,
                appliedFilters: [...state.appliedFilters].concat(action.payload.filter)
            };

        case ActionTypes.REMOVE_APPLIED_FILTERS:
            const {filterName, value} = action.payload;
            return {
                ...state,
                appliedFilters: state.appliedFilters.filter(item => {
                    return !(item.option.value === value && item.name === filterName);
                })
            };

        case ActionTypes.SET_ACTIVE_STATE:
            return {
                ...state,
                listResponse: action.payload.rules,
                automationRules: state.automationRules.map(rule => {
                    return updateIsActiveRule(rule, action.payload);
                }),
                filteredRules: state.filteredRules.map(rule => {
                    return updateIsActiveRule(rule, action.payload);
                })
            };

        case ActionTypes.HANDLE_SEARCH:
            return {
                ...state,
                searchValue: action.payload.searchValue
            };

        case ActionTypes.SAVE_RULE:
            const {rules} = action.payload;
            const mappedResponse = prepareListData(
                rules,
                state.choosesOptions,
                state.automationType
            );

            return {
                ...state,
                listResponse: rules,
                automationRules: mappedResponse,
                filteredRules: mappedResponse
            };
        case ActionTypes.SET_SELECTED_RULE_INDEX:
            return {
                ...state,
                selectedRuleIndex: action.payload.index
            };

        case ActionTypes.SET_AI_RULE_SUGGESTION:
            return {
                ...state,
                ...action.payload,
                aiGeneratedRuleId: Date.now().toString()
            };
        case ActionTypes.DISPATCH_STATE:
            return {
                ...state,
                ...action.payload
            };
        case ActionTypes.SET_COPYING_STATE:
            return {
                ...state,
                isCopying: action.payload.isCopying
            };

        case ActionTypes.HANDLE_RULE_FILTERS:
            const {searchValue, automationRules: defaultRules, appliedFilters} = state;
            const searchString = searchValue.toLocaleLowerCase();

            const searchedRules = searchValue
                ? defaultRules.filter(item => alertListFilter(item, searchString))
                : defaultRules;

            let filteredRules = searchedRules;

            if (appliedFilters.length > 0) {
                const appliedValuesByGroup = mapAppliedFilters(appliedFilters);
                const groupKeys = Object.keys(appliedValuesByGroup);

                filteredRules = searchedRules.filter(rule => {
                    return groupKeys.every(filerName => {
                        const filterGroup = appliedValuesByGroup[filerName];

                        return filterGroup.some(filter => {
                            const ruleValue = rule[filerName];
                            const filterValue = filter.value;

                            if (typeof ruleValue === 'string' && typeof filterValue === 'string') {
                                const ruleValueLowerCase = ruleValue.toLocaleLowerCase();
                                const filterValueLowerCase = filterValue.toLocaleLowerCase();

                                if (filerName === RuleFilters.validFor) {
                                    return isRuleAppliedToCurrentUser(
                                        ruleValueLowerCase,
                                        filterValueLowerCase
                                    );
                                }

                                return ruleValueLowerCase === filterValueLowerCase;
                            } else if (typeof ruleValue === 'boolean') {
                                return Boolean(filterValue) === ruleValue;
                            }
                            return filterValue === ruleValue;
                        });
                    });
                });
            }

            return {
                ...state,
                filteredRules
            };

        case ActionTypes.SET_EMAIL_TEMPLATE_FIELDS:
            const {emailTemplateFields} = action.payload;

            return {
                ...state,
                emailTemplateFields
            };

        default:
            return state;
    }
};
